
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, from as observableFrom, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationModalComponent } from 'src/app/common-components/modals/confirmation-modal/confirmation-modal.component';
import { GenericModal } from 'src/app/interface/data.interface';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  dataService: any;
  authService: any;
  fbToken: string = '';
  private userCache: any = null;
  private cacheTimestamp: number = 0;
  private readonly CACHE_DURATION = 10000; // 10 segundos para el interceptor
  private readonly INVALID_CREDENTIALS_EXCEPTION = 'invalid-credentials-exception';
  private readonly RETRY_ATTEMPTED_FLAG = 'X-Retry-Attempted';
  private dialogRefFirebaseMismatch: MatDialogRef<ConfirmationModalComponent, boolean> | null = null;

  constructor(
    public afAuth: AngularFireAuth,
    private injector: Injector,
  ) {
    this.initializeFbToken();
  }

  private initializeFbToken(): void {
    try {
      const user = JSON.parse(localStorage.getItem('currentUser'));
      if (user && user.fbToken) {
        this.fbToken = user.fbToken;
      }
    } catch {
      /* ignore */
    }

    // Escuchar cambios en el estado de autenticación
    this.afAuth.authState.subscribe((fbUser) => {
      if (fbUser) {
        fbUser.getIdToken(false).then((token) => {
          let currentUser: any = null;
          try {
            const raw = localStorage.getItem('currentUser');
            currentUser = raw ? JSON.parse(raw) : null;
          } catch {
            currentUser = null;
          }

          const fbEmail = (fbUser.email ?? '').toLowerCase().trim();
          const curEmail = (currentUser?.email ?? '').toLowerCase().trim();
          const sameFirebaseAccount = fbEmail && curEmail && fbEmail === curEmail;

          // En multisesión, currentUser puede ser otra cuenta que la sesión activa de Firebase;
          // no sobrescribir el fb-token persistido con el de otra cuenta.
          if (sameFirebaseAccount && currentUser) {
            this.fbToken = token;
            if (currentUser.fbToken !== token) {
              currentUser.fbToken = token;
              localStorage.setItem('currentUser', JSON.stringify(currentUser));
              this.userCache = null;
            }
          } else if (currentUser?.fbToken) {
            this.fbToken = currentUser.fbToken;
          }
        }).catch((error) => {
          console.error('Error getting Firebase token:', error);
          if (error.code === 'auth/user-token-expired' || error.code === 'auth/network-request-failed') {
            try {
              const currentUser = JSON.parse(localStorage.getItem('currentUser'));
              if (currentUser) {
                console.warn('Firebase token expired or network error, clearing session');
              }
            } catch {
              /* ignore */
            }
          }
        });
      } else {
        this.fbToken = '';
      }
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Si la request ya tiene la bandera de reintento, pasarla directamente
    // sin procesarla de nuevo para evitar duplicados
    if (req.headers.has(this.RETRY_ATTEMPTED_FLAG)) {
      return next.handle(req);
    }
    
    this.dataService = this.injector.get(DataService);
    this.authService = this.injector.get(AuthService);

    return observableFrom(this.handleAccess(req, next));
  }

  private async handleAccess(request: HttpRequest<any>, next: HttpHandler): Promise<HttpEvent<any>> {
    // Si la request ya tiene la bandera de reintento, no aplicar credenciales de nuevo
    // para evitar procesarla dos veces (defensa en profundidad)
    if (request.headers.has(this.RETRY_ATTEMPTED_FLAG)) {
      return next.handle(request).toPromise();
    }

    // Tras logout no debe quedar usuario en caché: si no hay currentUser, invalidar caché.
    if (!localStorage.getItem('currentUser')) {
      this.userCache = null;
      this.cacheTimestamp = 0;
    }
    
    const lang = this.dataService.getAppLanguage();
    
    // Leer siempre desde DataService para evitar desincronización de sesión en multisession.
    // DataService ya maneja su propio caché y detección de cambios en currentUser.
    const user = this.dataService.getUser();
    this.userCache = user;
    this.cacheTimestamp = Date.now();
    
    if (user) {
      // Priorizar el token de la sesión activa para no mezclar tokens entre cuentas.
      const fbTokenToUse = user.fbToken || this.fbToken;
      
      // Solo actualizar el objeto user en memoria para esta petición si el token cambió
      if (fbTokenToUse && user.fbToken !== fbTokenToUse) {
        user.fbToken = fbTokenToUse;
      }

      // Mantener sincronizado el token en memoria del interceptor con la sesión activa.
      if (fbTokenToUse && this.fbToken !== fbTokenToUse) {
        this.fbToken = fbTokenToUse;
      }
      
      const token = user.token;
      let building = null;
      let language = null;
      
      if (!user.superUser && user.buildings && user.buildings.length > 0) {
        building = user.buildings[0]._id;
      }
      
      if (lang) {
        language = lang;
      }

      if(localStorage.getItem('currentUser')) this.authService.openModalDatabaseUseAuthorization();

      request = this.applyCredentials(request, fbTokenToUse, token, building, language);
    }

    // Manejar errores de credenciales invalidas (Unauthorized) para refrescar token automáticamente
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Si el error ya fue marcado como "token refresh attempted", no intentar renovar de nuevo
        const alreadyAttempted = (error as any).__tokenRefreshAttempted === true;
        
        // Verificar si es un error de credenciales inválidas y si debemos intentar renovar el token
        const errorCode = error.error?.error?.code || error.error?.code;
        const hasRetryFlag = request.headers.has(this.RETRY_ATTEMPTED_FLAG);
        
        if (errorCode && errorCode.includes(this.INVALID_CREDENTIALS_EXCEPTION) && user && !hasRetryFlag && !alreadyAttempted) {
          return this.handleInvalidCredentialsError(request, next, user);
        }
        return throwError(() => error);
      })
    ).toPromise();
  }

  // Maneja errores de credenciales invalidas intentando refrescar el token de Firebase
  private handleInvalidCredentialsError(
    request: HttpRequest<any>,
    next: HttpHandler,
    user: any
  ): Observable<HttpEvent<any>> {
    return observableFrom(this.afAuth.currentUser).pipe(
      switchMap((fbUser: any) => {
        const curEmail = (user?.email ?? '').toLowerCase().trim();
        const fbEmail = (fbUser?.email ?? '').toLowerCase().trim();
        if (!fbUser || !curEmail || fbEmail !== curEmail) {
          return throwError(() => new Error('firebase-session-mismatch'));
        }
        return observableFrom(this.authService.hasActiveFirebaseSession()).pipe(
          switchMap((hasSession: boolean) => {
            if (hasSession) {
              return observableFrom(this.authService.refreshFirebaseToken()).pipe(
                switchMap((newToken: string | null) => {
                  if (newToken) {
                    // refreshFirebaseToken() ya actualiza el usuario en localStorage,
                    // pero necesitamos obtener el usuario actualizado para asegurar consistencia
                    this.dataService.clearUserCache();
                    const updatedUser = this.dataService.getUser();

                    // Si no se pudo obtener el usuario actualizado, usar el que teníamos
                    const userToUse = updatedUser || user;

                    // Asegurar que el token esté actualizado en el objeto usuario
                    if (userToUse) {
                      userToUse.fbToken = newToken;
                      // Actualizar currentUser en localStorage
                      this.dataService.setUser(userToUse);

                      // Actualizar usersSessions si el usuario está en esa lista
                      this.updateUserInMultisession(userToUse);
                    }

                    const lang = this.dataService.getAppLanguage();
                    let building = null;
                    let language = null;

                    if (userToUse && !userToUse.superUser && userToUse.buildings && userToUse.buildings.length > 0) {
                      building = userToUse.buildings[0]._id;
                    }

                    if (lang) {
                      language = lang;
                    }

                    // Actualizar el token en el interceptor
                    this.fbToken = newToken;

                    // Limpiar caché del interceptor para forzar actualización
                    this.userCache = null;
                    this.cacheTimestamp = 0;

                    // Crear nueva petición con el token renovado y bandera de reintento
                    // para evitar bucles infinitos
                    const newRequest = this.applyCredentials(
                      request,
                      newToken,
                      userToUse?.token || user.token,
                      building,
                      language
                    ).clone({
                      setHeaders: {
                        [this.RETRY_ATTEMPTED_FLAG]: 'true',
                      },
                    });

                    // Reintentar la petición UNA SOLA VEZ con el nuevo token
                    // Si falla, el error se marca para evitar que se procese de nuevo
                    return next.handle(newRequest).pipe(
                      catchError((retryError: HttpErrorResponse) => {
                        // Marcar el error para evitar que se procese de nuevo en handleAccess
                        const markedError = new HttpErrorResponse({
                          error: retryError.error,
                          status: retryError.status,
                          statusText: retryError.statusText,
                          url: retryError.url,
                          headers: retryError.headers,
                        });
                        (markedError as any).__tokenRefreshAttempted = true;
                        return throwError(() => markedError);
                      })
                    );
                  }
                  return throwError(() => new Error('Token refresh failed'));
                })
              );
            }
            return throwError(() => new Error('No active Firebase session'));
          })
        );
      }),
      catchError((error) => {
        console.error('Error en handleInvalidCredentialsError:', error);
        if (error instanceof Error && error.message === 'firebase-session-mismatch') {
          this.openFirebaseSessionMismatchModal();
        }
        return throwError(() => error);
      })
    );
  }

  private openFirebaseSessionMismatchModal(): void {
    if (this.dialogRefFirebaseMismatch) return;

    const matDialog = this.injector.get(MatDialog);
    const translate = this.injector.get(TranslateService);

    const modalData: GenericModal = {
      title: 'auth.sessionMismatch.title',
      subtitle: translate.instant('auth.sessionMismatch.subtitle'),
      note: 'auth.sessionMismatch.note',
      labelButtonCancel: 'buttons.close',
      labelButtonSave: 'auth.sessionMismatch.logoutCta',
      size: 'small',
    };

    this.dialogRefFirebaseMismatch = matDialog.open(ConfirmationModalComponent, {
      data: modalData,
      panelClass: ConfirmationModalComponent.getModalPanelClass(modalData.size, !!modalData.subtitle),
      autoFocus: false,
      restoreFocus: true,
    });

    this.dialogRefFirebaseMismatch.afterClosed().subscribe((result) => {
      this.dialogRefFirebaseMismatch = null;
      if (result) {
        // Logout forzado si el usuario acepta
        this.authService.doLogout();
      }
    });
  }

  // Actualiza el usuario en usersSessions si existe
  private updateUserInMultisession(user: any): void {
    try {
      const sessions = this.dataService.getUserInMultisession();
      if (sessions && sessions.length > 0) {
        const sessionIndex = sessions.findIndex((s: any) => s.email === user.email);
        if (sessionIndex !== -1) {
          // Actualizar el token en la sesión correspondiente
          sessions[sessionIndex].fbToken = user.fbToken;
          this.dataService.setUserInMultisesion(sessions[sessionIndex]);
        }
      }
    } catch (error) {
      console.error('Error actualizando usersSessions:', error);
    }
  }

  applyCredentials(req: HttpRequest<any>, fbToken, token, buildingId, language) {
    const items: any = {};
    
    if (token) {
      items['Authorization'] = `Bearer ${token}`;
    }
    
    if (fbToken) {
      items['Fb-Token'] = fbToken;
    }
    
    if (buildingId) {
      items['buildingId'] = buildingId;
    }
    
    if (language) {
      items['munilyLanguage'] = language;
    }
    
    items['platform'] = 'webpage';
    
    return req.clone({ setHeaders: items });
  }
}
